// ---------------------------------------------
// Quick Filters - Client-side Product Filtering
// ---------------------------------------------

import { $, $$, debounce } from '../utils/helpers.js';

let allProductData = {};
let isFiltered = false;
let currentPreset = null;

// Simple helper to check if URL has filters
function urlHasFilters() {
  const url = new URL(window.location.href);
  return url.searchParams.has('filter') || 
         url.searchParams.has('sort_by') || 
         url.searchParams.has('q');
}

// Remove all is-active from quick filters
function deactivateAllQuickFilters() {
  document.querySelectorAll('.quick-filter.is-active').forEach(btn => {
    btn.classList.remove('is-active');
  });
  isFiltered = false;
  currentPreset = null;
}

export async function initQuickFilters() {
  // If no URL filters, ensure no buttons are active
  if (!urlHasFilters()) {
    deactivateAllQuickFilters();
  }

  const container = document.querySelector('[data-quick-filters-container]');
  if (!container) return;

  allProductData = {};

  const presetButtons = container.querySelectorAll('.quick-filter[data-qf-preset]');
  const urlButtons = container.querySelectorAll('.quick-filter[href]:not([data-qf-preset])');

  await loadAllProducts();
  
  if (Object.keys(allProductData).length === 0) {
    loadProductsFromDOM();
  }

  if (presetButtons.length > 0) {
    processPresetButtons(presetButtons);
  }

  if (urlButtons.length > 0) {
    await processUrlButtons(urlButtons);
  }
}

function processPresetButtons(filterButtons) {
  filterButtons.forEach(button => {
    button.addEventListener('click', (e) => {
      const preset = button.dataset.qfPreset;
      
      if (button.classList.contains('is-active')) {
        button.classList.remove('is-active');
        isFiltered = false;
        currentPreset = null;
        showAllProducts();
        return;
      }
      
      filterButtons.forEach(btn => btn.classList.remove('is-active'));
      button.classList.add('is-active');
      isFiltered = true;
      currentPreset = preset;

      filterProductsByPreset(preset);
    });
  });
}

async function loadAllProducts() {
  const collectionHandle = window.__qfCollectionHandle__;
  if (!collectionHandle) {
    loadProductsFromDOM();
    return;
  }

  try {
    const response = await fetch(`/collections/${collectionHandle}.json`);
    if (!response.ok) {
      loadProductsFromDOM();
      return;
    }

    const data = await response.json();
    const products = data.collection?.products || [];

    if (products.length > 0) {
      products.forEach(product => {
        const discountPercent = calculateDiscountPercent(product);
        allProductData[product.id] = {
          id: String(product.id),
          price: product.price || 0,
          compareAtPrice: product.compare_at_price || 0,
          discountPercent: discountPercent,
          available: product.available,
          tags: product.tags || []
        };
      });
    } else {
      loadProductsFromDOM();
    }
  } catch (error) {
    loadProductsFromDOM();
  }
}

function calculateDiscountPercent(product) {
  if (!product.compare_at_price || !product.price) return 0;
  if (product.compare_at_price <= product.price) return 0;
  const savings = product.compare_at_price - product.price;
  return Math.round((savings / product.compare_at_price) * 100);
}

function loadProductsFromDOM() {
  const productGrid = $('#ProductGridContainer .collection, #product-grid');
  if (!productGrid) return;

  const cards = $$('.dss-product-card', productGrid);
  cards.forEach(card => {
    allProductData[card.dataset.productId] = {
      id: card.dataset.productId,
      price: parseInt(card.dataset.price) || 0,
      compareAtPrice: parseInt(card.dataset.compareAtPrice) || 0,
      discountPercent: parseInt(card.dataset.discountPercent) || 0,
      available: card.dataset.available !== 'false',
      tags: card.dataset.tags ? card.dataset.tags.split(',') : []
    };
  });
}

function countProductsByPreset(preset) {
  let count = 0;
  Object.values(allProductData).forEach(product => {
    if (matchesPreset(product, preset)) count++;
  });
  return count;
}

function matchesPreset(product, preset) {
  const discountPercent = product.discountPercent || 0;
  const compareAtPrice = product.compareAtPrice || 0;
  const price = product.price || 0;
  
  switch (preset) {
    case 'min_10_percent': return discountPercent >= 10;
    case 'min_20_percent': return discountPercent >= 20;
    case 'min_30_percent': return discountPercent >= 30;
    case 'min_50_percent': return discountPercent >= 50;
    case 'on_sale': return compareAtPrice > price && price > 0;
    case 'in_stock': return product.available !== false;
    case 'new': return product.tags && product.tags.includes('new');
    default: return true;
  }
}

function filterProductsByPreset(preset) {
  const productGrid = $('#ProductGridContainer .collection, #product-grid');
  if (!productGrid) return;

  const existingEmpty = productGrid.querySelector('.quick-filters-empty-state');
  if (existingEmpty) existingEmpty.remove();

  const filteredIds = new Set();
  Object.entries(allProductData).forEach(([id, product]) => {
    if (matchesPreset(product, preset)) filteredIds.add(id);
  });

  const cards = $$('.dss-product-card', productGrid);
  cards.forEach(card => {
    const productId = card.dataset.productId;
    const show = filteredIds.has(productId);
    card.style.display = show ? '' : 'none';
    card.classList.toggle('qf-hidden', !show);
    const parent = card.closest('.product-grid-item') || card.closest('.grid__item');
    if (parent) parent.style.display = show ? '' : 'none';
  });

  if (filteredIds.size === 0) {
    showEmptyState(productGrid);
  }

  updateProductCount(filteredIds.size);
}

function showEmptyState(container) {
  const emptyState = document.createElement('div');
  emptyState.className = 'quick-filters-empty-state';
  emptyState.innerHTML = `
    <div class="quick-filters-empty-state__icon">
      <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
        <circle cx="11" cy="11" r="8"></circle>
        <path d="m21 21-4.35-4.35"></path>
        <path d="M8 8l6 6"></path>
        <path d="M14 8l-6 6"></path>
      </svg>
    </div>
    <h3 class="quick-filters-empty-state__title">Leider haben wir kein passendes Produkt gefunden</h3>
    <p class="quick-filters-empty-state__text">Versuche einen anderen Filter oder entferne Filter um alle Produkte zu sehen.</p>
    <button class="quick-filters-empty-state__reset" type="button">
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path>
        <path d="M3 3v5h5"></path>
      </svg>
      Filter zurücksetzen
    </button>
  `;
  
  container.appendChild(emptyState);
  requestAnimationFrame(() => emptyState.classList.add('is-visible'));

  const resetBtn = emptyState.querySelector('.quick-filters-empty-state__reset');
  if (resetBtn) {
    resetBtn.addEventListener('click', () => {
      const activeBtn = document.querySelector('.quick-filter.is-active');
      if (activeBtn) activeBtn.click();
    });
  }
}

function showAllProducts() {
  const productGrid = $('#ProductGridContainer .collection, #product-grid');
  if (!productGrid) return;

  const emptyState = productGrid.querySelector('.quick-filters-empty-state');
  if (emptyState) {
    emptyState.classList.remove('is-visible');
    setTimeout(() => emptyState.remove(), 300);
  }

  const cards = $$('.dss-product-card', productGrid);
  cards.forEach(card => {
    card.style.display = '';
    card.classList.remove('qf-hidden');
    const parent = card.closest('.product-grid-item') || card.closest('.grid__item');
    if (parent) parent.style.display = '';
  });

  const countElement = $('#ProductCount, #ProductCountDesktop');
  if (countElement && countElement.dataset.originalCount) {
    countElement.textContent = countElement.dataset.originalCount;
  } else {
    updateProductCount(Object.keys(allProductData).length);
  }
}

function updateProductCount(visibleCount) {
  const countElement = $('#ProductCount, #ProductCountDesktop');
  if (countElement) {
    const originalText = countElement.dataset.originalCount || countElement.textContent;
    if (!countElement.dataset.originalCount) countElement.dataset.originalCount = originalText;
    countElement.textContent = originalText.replace(/\d+/, visibleCount);
  }
}

// CORE: On EVERY page load/interaction, if no URL filters → no active buttons
function syncQuickFilterState() {
  if (!urlHasFilters()) {
    deactivateAllQuickFilters();
  }
}

// Debounced sync for high-frequency events (scroll, resize) to prevent
// main-thread blocking — especially critical on iOS Safari where momentum
// scrolling fires dozens of events per frame, each triggering forced layout.
const debouncedSync = debounce(syncQuickFilterState, 150);

['load', 'shopify:section:load', 'popstate'].forEach(event => {
  window.addEventListener(event, syncQuickFilterState);
});

window.addEventListener('scroll', debouncedSync, { passive: true });
window.addEventListener('resize', debouncedSync, { passive: true });

// "Alle entfernen" clicked
document.addEventListener('click', (e) => {
  if (e.target.closest('.active-facets__button--clear')) {
    syncQuickFilterState();
  }
});

// PERMANENT: Interval der sicherstellt dass State immer korrekt ist
setInterval(() => {
  // Nur deaktivieren wenn: keine URL-Filter UND kein dynamischer Filter aktiv
  if (!urlHasFilters() && !isFiltered) {
    document.querySelectorAll('.quick-filter.is-active').forEach(btn => {
      btn.classList.remove('is-active');
    });
  }
}, 2000);

async function processUrlButtons(urlButtons) {
  const collectionHandle = window.__qfCollectionHandle__;
  if (!collectionHandle) return;

  for (const button of urlButtons) {
    const href = button.getAttribute('href');
    if (!href) continue;

    // Skip external URLs or anchors
    if (href.startsWith('http') || href.startsWith('//') || href.startsWith('#')) continue;

    // Convert to button for JS handling
    button.setAttribute('type', 'button');
    button.classList.add('quick-filter--url');

    button.addEventListener('click', async (e) => {
      e.preventDefault();

      // Build full URL
      let fullUrl;
      if (href.startsWith('?')) {
        fullUrl = `/collections/${collectionHandle}${href}`;
      } else if (href.startsWith('/')) {
        fullUrl = href;
      } else {
        window.location.href = href;
        return;
      }

      // Show loading state
      button.classList.add('is-loading');

      try {
        const response = await fetch(fullUrl);
        if (!response.ok) {
          window.location.href = fullUrl;
          return;
        }

        const html = await response.text();

        // Find product count
        let productCount = 0;
        const patterns = [
          /data-product-count="(\d+)"/,
          /product_count[^>]*>(\d+)/,
          /(\d+)\s*produkte?/i,
          /(\d+)\s*products?/i
        ];

        for (const pattern of patterns) {
          const match = html.match(pattern);
          if (match) {
            productCount = parseInt(match[1], 10);
            break;
          }
        }

        if (productCount === 0) {
          // Show our empty state instead of navigating
          showUrlFilterEmptyState(fullUrl, button);
        } else {
          // Navigate to URL
          window.location.href = fullUrl;
        }
      } catch (error) {
        // On error, navigate normally
        window.location.href = fullUrl;
      } finally {
        button.classList.remove('is-loading');
      }
    });
  }
}

function showUrlFilterEmptyState(url, clickedButton) {
  // Remove any existing empty state
  const existingEmpty = document.querySelector('.quick-filters-empty-state');
  if (existingEmpty) existingEmpty.remove();

  const productGrid = $('#ProductGridContainer .collection, #product-grid');
  if (!productGrid) return;

  const emptyState = document.createElement('div');
  emptyState.className = 'quick-filters-empty-state';
  emptyState.innerHTML = `
    <div class="quick-filters-empty-state__icon">
      <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="11" cy="11" r="8"></circle>
        <path d="m21 21-4.35-4.35"></path>
        <path d="M8 8l6 6"></path>
        <path d="M14 8l-6 6"></path>
      </svg>
    </div>
    <h3 class="quick-filters-empty-state__title">Leider haben wir kein passendes Produkt gefunden</h3>
    <p class="quick-filters-empty-state__text">Versuche einen anderen Filter oder entferne Filter um alle Produkte zu sehen.</p>
    <button class="quick-filters-empty-state__reset" type="button">
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path>
        <path d="M3 3v5h5"></path>
      </svg>
      Filter zurücksetzen
    </button>
  `;
  
  productGrid.appendChild(emptyState);

  requestAnimationFrame(() => {
    emptyState.classList.add('is-visible');
  });

  // Reset button - go to collection without filters
  const resetBtn = emptyState.querySelector('.quick-filters-empty-state__reset');
  if (resetBtn) {
    resetBtn.addEventListener('click', () => {
      const collectionHandle = window.__qfCollectionHandle__;
      window.location.href = `/collections/${collectionHandle}`;
    });
  }
}
